import React, { useMemo, useState } from "react";

import { ArrayUtils, useGetFarmCoverCropDetections, useGetFarmDocScanSummary, useGetFarmMainCropDetections, useGetVerificationEditingRequests } from "utils";

import { EDITING_REQUEST_TYPE, FieldVerification, PRACTICE_SLUG, VerificationEditingRequest } from "models";

import { Row, Tag } from "components";

import { DocScanSummaryCard } from "pages/admin/verifications/components/automations/DocScanSummaryCard";
import { FieldEvidenceDrawer, FieldEvidenceSelection } from "pages/admin/verifications/components/automations/FieldEvidenceDrawer";
import { PracticeList } from "pages/admin/verifications/components/lists/PracticeList";
import { CoverCropAutomationCard } from "pages/admin/verifications/components/misc/CoverCropAutomationCard";
import { CropTable } from "pages/admin/verifications/components/misc/CropTable";
import { CropTimeline } from "pages/admin/verifications/components/misc/CropTimeline";
import { EditingRequestInsightCards } from "pages/admin/verifications/components/misc/EditingRequestInsightCards";
import { VerificationAutomations } from "pages/admin/verifications/components/misc/VerificationAutomations";
import { COVER_CROP_DEFAULT_MIN_CONFIDENCE } from "pages/admin/verifications/utils/coverCropChips";

export const CropList = ({ farmId, onUpdate }: { farmId: string; onUpdate?: () => void }) => {
  const { data: editingRequests, isLoading: isLoadingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  const { data: coverCropDetections } = useGetFarmCoverCropDetections({ farmId });
  const [chosenMinConfidence, setChosenMinConfidence] = useState<number | null>(null);
  const minCoverCropConfidence =
    chosenMinConfidence ?? coverCropDetections?.find(({ modelThreshold }) => modelThreshold !== null)?.modelThreshold ?? COVER_CROP_DEFAULT_MIN_CONFIDENCE;
  const coverCropDetectionsByField = useMemo(() => ArrayUtils.groupBy(coverCropDetections ?? [], ({ fieldId }) => fieldId), [coverCropDetections]);
  const hasCoverCropDetections = !!coverCropDetections?.length;
  // Only the evidence drawer reads these. Loading them now means it finds them cached.
  useGetFarmMainCropDetections({ farmId });

  const { data: docScanSummary } = useGetFarmDocScanSummary({ farmId });
  const hasDocScans = !!docScanSummary?.years.length;

  // The selection outlives the drawer so its content stays put while it slides out
  const [evidenceSelection, setEvidenceSelection] = useState<FieldEvidenceSelection | null>(null);
  const [isEvidenceOpen, setIsEvidenceOpen] = useState(false);
  const openEvidence = (selection: FieldEvidenceSelection) => {
    setEvidenceSelection(selection);
    setIsEvidenceOpen(true);
  };

  // Helper function to get editing requests for a specific field
  const getFieldEditingRequests = (fieldId: string): VerificationEditingRequest[] => editingRequests?.filter((request) => request.fieldId === fieldId) ?? [];

  // Helper function to get crop-level editing request status for a field
  const getCropEditingRequestStatus = (fieldId: string) => {
    const cropEditingRequests = editingRequests?.filter((request) => request.fieldId === fieldId && request.fieldCropId) ?? [];
    const missingCropRequests =
      editingRequests?.filter((request) => request.fieldId === fieldId && request.requestType === EDITING_REQUEST_TYPE.CROP && !request.fieldCropId) ?? [];
    const allEditingRequests = [...cropEditingRequests, ...missingCropRequests];

    if (allEditingRequests.length === 0) {
      return null;
    }

    const hasUncompletedRequests = allEditingRequests.some((req) => !req.completedAt);
    const hasUnreviewedRequests = allEditingRequests.some((req) => req.completedAt && !req.reviewedAt);
    const hasCompletedRequests = allEditingRequests.some((req) => req.completedAt && req.reviewedAt);

    if (hasUncompletedRequests) {
      return { text: "open request", colour: "red" as const };
    }

    if (hasUnreviewedRequests) {
      return { text: "review request", colour: "orange" as const };
    }

    if (hasCompletedRequests) {
      return { text: "completed request", colour: "blue" as const };
    }

    return null;
  };

  return (
    <>
      <Row className="padding-bottom-200">
        {/* prelint-ignore-col */}
        {/* Cols are inside component */}
        <EditingRequestInsightCards loading={isLoadingEditingRequests} editingRequests={editingRequests ?? []} requestTypes={[EDITING_REQUEST_TYPE.CROP]} />
      </Row>

      {(hasCoverCropDetections || hasDocScans) && (
        <VerificationAutomations>
          {hasCoverCropDetections && (
            <CoverCropAutomationCard detections={coverCropDetections} minConfidence={minCoverCropConfidence} onMinConfidenceChange={setChosenMinConfidence} />
          )}
          <DocScanSummaryCard farmId={farmId} />
        </VerificationAutomations>
      )}

      <PracticeList
        farmId={farmId}
        practiceSlugs={[PRACTICE_SLUG.CROPS]}
        onUpdate={onUpdate}
        renderExtraColumns={(field: FieldVerification) => {
          const editingRequestStatus = getCropEditingRequestStatus(field.id);
          return editingRequestStatus ? <Tag colour={editingRequestStatus.colour} text={editingRequestStatus.text} /> : null;
        }}
        renderTimeline={(fieldId, field) => (
          <CropTimeline
            fieldId={fieldId}
            fieldName={field.name}
            farmId={farmId}
            coverCropDetections={coverCropDetectionsByField[fieldId]}
            minCoverCropConfidence={minCoverCropConfidence}
            onOpenEvidence={openEvidence}
          />
        )}
        renderExpandedContent={(field) => (
          <CropTable field={field} farmId={farmId} editingRequests={getFieldEditingRequests(field.id)} onOpenEvidence={openEvidence} />
        )}
        additionalDrawerProps={{
          editingRequests: editingRequests ?? [],
        }}
      />

      <FieldEvidenceDrawer farmId={farmId} selection={evidenceSelection} isOpen={isEvidenceOpen} onClose={() => setIsEvidenceOpen(false)} />
    </>
  );
};
